/**
 * Account Settings — /(app)/settings/account
 *
 * A profile header (identity, public profile link, inline name/email editing),
 * then two grouped cards: Gameplay (vacation, notifications) and Privacy &
 * display (blocked users, preferred colour, theme).
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React from 'react'
import { View, Text, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../hooks/useOrientation'
import { useAuth } from '../../../hooks/useAuth'
import { ThemeToggle } from '../../../components/settings/ThemeToggle'
import { goBackOr } from '../../../lib/goBack'
import { isVacationActive } from '../../../lib/vacation'
import { VacationSettingsModal } from '../../../components/settings/VacationSettingsModal'
import { DefaultColorPicker } from '../../../components/settings/DefaultColorPicker'
import { VacationStatusChip } from '../../../components/settings/VacationStatusChip'
import { PLAYER_COLORS, clampPlayerSlot } from '@warcarp/ui'

/** Header links are short text; widen the touch target to ~44pt. */
const LINK_HIT_SLOP = { top: 12, bottom: 12, left: 8, right: 8 }

/** Small olive caption above a grouped settings card. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return <Text accessibilityRole="header" className="font-stencil text-[10px] uppercase tracking-widest text-olive-up" style={{ marginLeft: 6 }}>{children}</Text>
}

interface RowProps {
  label: string
  preview: string | React.ReactNode
  onPress: () => void
  accessibilityLabel: string
  editAction?: boolean
  actionLabel?: 'Edit' | 'Manage' | 'View'
  /**
   * Short state shown next to the label on the LEFT (e.g. vacation Off / On),
   * leaving the right side for the action link like every other row.
   */
  status?: string | React.ReactNode
  /**
   * Whether to draw the top divider line. The first row in a card omits it
   * because the card already has its own outer border. Defaults to true.
   */
  borderTop?: boolean
}

function SettingsRow({ label, preview, onPress, accessibilityLabel, borderTop = true, editAction = false, actionLabel, status }: RowProps) {
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      className={`flex-row items-center justify-between px-4 py-4 ${borderTop ? 'border-t border-olive' : ''}`}
      style={{ minHeight: 56 }}
    >
      <View className="flex-row items-center flex-1 mr-3" style={{ gap: 10, minWidth: 0 }}>
        <Text className="font-stencil text-ink text-sm uppercase shrink">{label}</Text>
        {typeof status === 'string' ? <Text className="font-typed text-ink-soft text-sm" numberOfLines={1}>{status}</Text> : status}
      </View>
      <View className="flex-row items-center gap-2" style={{ maxWidth: editAction || actionLabel ? '58%' : '50%' }}>
        {typeof preview === 'string' ? (
          <Text className="font-typed text-ink-soft text-sm shrink" numberOfLines={1}>{preview}</Text>
        ) : (
          preview
        )}
        <Text className={editAction || actionLabel ? 'font-stencil text-blood text-xs' : 'text-ink-soft text-lg'}>{(actionLabel ?? (editAction ? 'Edit' : ''))}{editAction || actionLabel ? ' ›' : '›'}</Text>
      </View>
    </TouchableOpacity>
  )
}

export default function AccountSettingsScreen() {
  const { user } = useAuth()
  const [vacationOpen, setVacationOpen] = React.useState(false)
  const [identityOpen, setIdentityOpen] = React.useState(false)
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(480, winWidth * 0.55) : undefined
  const vacationActive = isVacationActive(user?.vacation, Date.now())
  // Avatar wears the preferred colour (white initial reads on every palette
  // colour); olive when the player has no preference.
  const preferredSlot = user?.preferredColorSlot ?? null
  const avatarColor = preferredSlot === null ? '#6b7e3e' : PLAYER_COLORS[clampPlayerSlot(preferredSlot)].hex
  const initial = (user?.username ?? '?').slice(0, 1).toUpperCase()
  const identityLine = [user?.realName, user?.email].filter(Boolean).join(' · ')

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {/* Header */}
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings')}
          accessibilityLabel="Back to settings"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">General settings</Text>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 16,
          gap: 20,
          alignItems: isLandscape ? 'center' : undefined,
        }}
        keyboardShouldPersistTaps="handled"
      >
        <View style={isLandscape ? { width: contentWidth, gap: 20 } : { width: '100%', gap: 20 }}>
        {/* Profile header: identity first, replacing the old "Public profile"
            and "Player names and email" rows. "Edit names" expands the three
            identity rows inside the same card. */}
        <View testID="account-profile-card" className="bg-paper rounded-2xl overflow-hidden">
          <View className="flex-row items-center px-4 py-4" style={{ gap: 14 }}>
            <View testID="account-avatar" style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: avatarColor, alignItems: 'center', justifyContent: 'center' }}>
              <Text className="font-stencil" style={{ color: '#ffffff', fontSize: 20 }}>{initial}</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text className="font-stencil text-ink text-base" numberOfLines={1}>{user?.username ?? ''}</Text>
              {!!identityLine && <Text className="font-typed text-ink-soft text-sm" numberOfLines={1}>{identityLine}</Text>}
              <TouchableOpacity onPress={() => setIdentityOpen(!identityOpen)} accessibilityRole="button"
                accessibilityLabel="Edit names and email" accessibilityState={{ expanded: identityOpen }} aria-expanded={identityOpen}
                hitSlop={LINK_HIT_SLOP} className="mt-2 self-start">
                <Text className="font-stencil text-blood text-xs uppercase">Edit names</Text>
              </TouchableOpacity>
            </View>
            {/* Right edge, same inset as the "Edit ›" / "Manage ›" links in the
                cards below, so the action links line up down the screen. */}
            <TouchableOpacity testID="account-view-profile" onPress={() => router.push('/(app)/settings/stats')} accessibilityRole="button"
              accessibilityLabel="View profile" hitSlop={LINK_HIT_SLOP}>
              <Text className="font-stencil text-blood text-xs uppercase">View profile ›</Text>
            </TouchableOpacity>
          </View>
          {identityOpen && <>
          <SettingsRow
            label="Player Name"
            preview={`@${user?.username ?? ''}`}
            onPress={() => router.push('/(app)/settings/account/name')}
            accessibilityLabel="Edit player name"
            editAction
          />

          {/* Real Name row — optional public-profile identity so friends can
              tell who is who. */}
          <SettingsRow
            label="Real Name"
            preview={user?.realName ?? 'Not set'}
            onPress={() => router.push('/(app)/settings/account/real-name')}
            accessibilityLabel="Edit real name"
            editAction
          />

          {/* Email row */}
          <SettingsRow
            label="Email"
            preview={user?.email ?? ''}
            onPress={() => router.push('/(app)/settings/account/email')}
            accessibilityLabel="Edit email"
            editAction
          />
          </>}
        </View>

        <View style={{ gap: 6 }}>
          <SectionLabel>Gameplay</SectionLabel>
          <View testID="account-gameplay-card" className="bg-paper rounded-2xl overflow-hidden">
            <SettingsRow label="Vacation mode" status={<VacationStatusChip active={vacationActive} />} preview={null}
              onPress={() => setVacationOpen(true)} accessibilityLabel="Open vacation settings" borderTop={false} actionLabel="Manage" />
            <SettingsRow
              label="Notifications"
              preview={null}
              onPress={() => router.push('/(app)/settings/notifications')}
              accessibilityLabel="Notifications"
              editAction
            />
          </View>
        </View>

        <View style={{ gap: 6 }}>
          <SectionLabel>Privacy &amp; display</SectionLabel>
          <View testID="account-privacy-display-card" className="bg-paper rounded-2xl overflow-hidden">
            <SettingsRow
              label="Blocked Users"
              preview={null}
              onPress={() => router.push('/(app)/settings/blocked-users')}
              accessibilityLabel="Blocked users"
              borderTop={false}
              actionLabel="Manage"
            />
            {/* The picker draws no divider of its own; this one matches the rows. */}
            <View testID="account-color-row" className="border-t border-olive">
              <DefaultColorPicker />
            </View>
            <ThemeToggle embedded />
          </View>
        </View>
        </View>
      </ScrollView>
      <VacationSettingsModal visible={vacationOpen} onClose={() => setVacationOpen(false)} />
    </SafeAreaView>
  )
}
